import hideOnClickOutside from 'PlugAndPlay/_global/js/clickOutside';
import whenKey from 'ally.js/when/key';
import debounce from 'PlugAndPlay/_global/js/debounce';

/**
 * Component selector.
 *
 * @type {string}
 */
const BUTTON_WRAPPER = '[data-component="OverflowButton"]';

/**
 * Builds overflow button object.
 *
 */
class OverflowButton {
    /**
     * @const {string} - The selector for the overflow menu button
     */
    static TAB_NAVIGATION_OVERFLOW_MENU_BUTTON_SELECTOR =
        '[data-tab-group-element="overflow-menu-button"]';

    /**
     * @const {string} - The overflow menu container selector
     */
    static TAB_NAVIGATION_OVERFLOW_MENU_CONTAINER_SELECTOR =
        '[data-tab-group-element="overflow-menu-container"]';

    /**
     * @const {string} - The overflow menu active class
     */
    static TAB_NAVIGATION_OVERFLOW_MENU_ACTIVE_CLASS =
        'overflow-menu__wrapper--visible';

    /**
     * @const {string} - Overflow menu open class
     */
    static TAB_NAVIGATION_OVERFLOW_MENU_OPEN_CLASS = 'overflow-menu--open';

    /**
     * @const {string} - Overflow menu button open class
     */
    static TAB_NAVIGATION_OVERFLOW_MENU_BUTTON_OPEN_CLASS =
        'overflow-menu__button--open';

    constructor(wrapper) {
        this.wrapper = wrapper;

        this.recalculateMenuOnResize = this.recalculateMenuOnResize.bind(this);
        this.toggleOverflowMenuState = this.toggleOverflowMenuState.bind(this);

        // Get the tab nav overflow button
        this.navigationOverflowMenuButton = this.wrapper.querySelector(
            OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_BUTTON_SELECTOR
        );

        // Get the container for the overflow menu
        this.navigationOverflowMenuContainer = this.wrapper.querySelector(
            OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_CONTAINER_SELECTOR
        );

        // Get the overflow menu wrapper
        this.navigationOverflowMenuWrapper = this.wrapper;

        const { container, itemsContainer, items } = this.wrapper.dataset;

        this.tabGroup = document.querySelector(container);
        this.navigationButtons = this.tabGroup.querySelectorAll(items);
        this.navigationContainer = this.tabGroup.querySelector(itemsContainer);

        // Define a variable if the overflow menu is active
        this.overflowMenuActive = false;

        // Destroy any previously set event listeners before initializing new ones
        this.removeEventListeners();

        // Add the event listeners
        this.addEventListeners();

        // Calculate the items that should be in the overflow menu
        this.addElementsToOverflowMenu();

        this.allyHandles = {
            keyHandle: undefined,
        };
    }

    /**
     *
     * @param {*} event
     */
    

    addElementsToOverflowMenu() {
        // get the width of the containing div
        const containerWidth = this.tabGroup.offsetWidth;

        // Move all the buttons to the navigation container (not the dropdown) so we can calculate the widths correctly
        this.navigationButtons.forEach((button) => {
            this.navigationContainer.append(button);
        });

        // Move the dropdown container to be the last item after appending the buttons
        this.navigationContainer.append(this.navigationOverflowMenuWrapper);

        // Get the width of each element
        const navigationButtonWidths = Array.from(this.navigationButtons).map(
            (button) => {
                // Return the button width including the margin left and right
                return (
                    button.offsetWidth +
                    parseInt(window.getComputedStyle(button).marginLeft, 10) +
                    parseInt(window.getComputedStyle(button).marginRight, 10)
                );
            }
        );

        // Get the navigation buttons total widths
        const navigationButtonsTotalWidths = navigationButtonWidths.reduce(
            (currentButtonWidthAccumulator, currentButtonWidth) =>
                currentButtonWidthAccumulator + currentButtonWidth,
            0 // Initial value to prevent error on empty array
        );

        // Get the width of the overflow menu button if the sum of the other buttons' widths is greater than the container width
        if (navigationButtonsTotalWidths > containerWidth) {
            // Get the overflow menu button width including the margins
            const overflowMenuButtonWidth =
                this.navigationOverflowMenuButton.offsetWidth +
                parseInt(
                    window.getComputedStyle(this.navigationOverflowMenuButton)
                        .marginLeft,
                    10
                ) +
                parseInt(
                    window.getComputedStyle(this.navigationOverflowMenuButton)
                        .marginRight,
                    10
                );

            // Get an array of the buttons that should be shown
            const buttonsToShow = [];
            // Get an array of the buttons that should be moved to the dropdown
            const buttonsToMoveToDropdown = [];
            // Variable to define if we can add more buttons
            let canAddMoreButtons = true;

            // Define the starting width of the sum of the buttons as 0
            let currentButtonsToShowWidth = 0;

            // Loop through the buttons
            this.navigationButtons.forEach((button) => {
                // Check if the button being added would push the other buttons over the width
                if (
                    currentButtonsToShowWidth +
                        button.offsetWidth +
                        parseInt(
                            window.getComputedStyle(button).marginLeft,
                            10
                        ) +
                        parseInt(
                            window.getComputedStyle(button).marginRight,
                            10
                        ) +
                        overflowMenuButtonWidth <
                        containerWidth &&
                    canAddMoreButtons
                ) {
                    // If the width isn't going to push over the button over the width, add the button to the buttons to show list
                    buttonsToShow.push(button);

                    // add the button width to the current buttons to show width
                    currentButtonsToShowWidth =
                        currentButtonsToShowWidth +
                        button.offsetWidth +
                        parseInt(
                            window.getComputedStyle(button).marginLeft,
                            10
                        ) +
                        parseInt(
                            window.getComputedStyle(button).marginRight,
                            10
                        );
                } else {
                    // If the button shouldn't be shown, add it to the array that should be moved to the dropdown
                    buttonsToMoveToDropdown.push(button);
                    // Set the can add more buttons to false (preventing any more buttons being added)
                    canAddMoreButtons = false;
                }
            });

            // Move the buttons to the dropdown
            buttonsToMoveToDropdown.forEach((button) => {
                button.setAttribute('tabindex', '-1');
                // eslint-disable-next-line no-param-reassign
                button.dataset.hidden = 'true';
                this.navigationOverflowMenuContainer.append(button);
            });

            buttonsToShow.forEach((button) => {
                button.setAttribute('tabindex', '0');
                // eslint-disable-next-line no-param-reassign
                button.dataset.hidden = 'false';
            });

            // Display the overflow menu
            this.showOverflowMenu(true);

            // Set that the overflow menu is active
            this.overflowMenuActive = true;
        } else {
            // Don't display the overflow menu
            this.showOverflowMenu(false);
            // Set that the overflow menu is inactive
            this.overflowMenuActive = false;
        }
    }





    /**
     * Resize Event listener to call the recalculation when the window width has changed
     */
    recalculateMenuOnResize() {
        // Call the add elements to overflow menu which will rework the menu
        this.addElementsToOverflowMenu();
    }

    /**
     * Function that adds the event listeners when the tab group is initialized
     * @method
     */
    addEventListeners() {
        // Add an event listener that re-works the menu when the window is resized
        window.addEventListener(
            'resize',
            debounce(this.recalculateMenuOnResize)
        );

        // Add event listener to open and close the overflow menu
        this.navigationOverflowMenuButton.addEventListener(
            'click',
            this.toggleOverflowMenuState
        );
    }

    /**
     * Function that removes the event listeners that are setup in the add event listeners function
     * @method
     */
    removeEventListeners() {
        // remove the event listener that re-works the menu when the window is resized
        window.removeEventListener(
            'resize',
            debounce(this.recalculateMenuOnResize)
        );

        // Remove the event listener for the overflow menu
        this.navigationOverflowMenuButton.removeEventListener(
            'click',
            this.toggleOverflowMenuState
        );
    }

    /**
     * Reworks tab index based on button visibility.
     *
     * @param {boolean} shouldBeTabbable Determines if the items are visible or hidden.
     */
    setTabIndexes(shouldBeTabbable) {
        this.navigationButtons.forEach((button) => {
            const shouldBeVisible =
                button.dataset.hidden === 'true' && shouldBeTabbable;
            const shouldBeHidden =
                button.dataset.hidden === 'true' && !shouldBeTabbable;

            if (shouldBeVisible) {
                button.setAttribute('tabindex', '0');
            }
            if (shouldBeHidden) {
                button.setAttribute('tabindex', '-1');
            }
        });
    }

    /**
     * Toggle the visibility state of the overflow menu
     * @method
     */
    toggleOverflowMenuState(event) {
        // Stop the propagation of the event so that we can attach the click outside event handler without it being called immediately
        event.stopPropagation();

        if (this.overflowMenuActive) {
            this.setTabIndexes(this.overflowMenuActive);

            this.navigationOverflowMenuContainer.classList.toggle(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_OPEN_CLASS
            );
            this.navigationOverflowMenuButton.classList.toggle(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_BUTTON_OPEN_CLASS
            );

            // Check if we have just opened the menu and if so add an event listener to close it if the user clicks outside it
            if (
                this.navigationOverflowMenuContainer.classList.contains(
                    OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_OPEN_CLASS
                )
            ) {
                hideOnClickOutside(this.navigationOverflowMenuContainer, () =>
                    this.closeOverflowMenu()
                );

                this.navigationOverflowMenuButton.setAttribute(
                    'aria-expanded',
                    true
                );

                this.allyHandles.keyHandle = whenKey({
                    escape: () => {
                        setTimeout(this.closeOverflowMenu());
                    },
                });
            } else {
                this.navigationOverflowMenuButton.setAttribute(
                    'aria-expanded',
                    false
                );

                if (this.allyHandles.keyHandle) {
                    this.allyHandles.keyHandle.disengage();
                }
            }
        }
    }

    /**
     * Closes the overflow menu
     * @method
     */
    closeOverflowMenu() {
        if (this.overflowMenuActive) {
            this.setTabIndexes(!this.overflowMenuActive);

            this.navigationOverflowMenuContainer.classList.remove(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_OPEN_CLASS
            );
            // Remove the open class from the navigation button
            this.navigationOverflowMenuButton.classList.remove(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_BUTTON_OPEN_CLASS
            );

            this.navigationOverflowMenuButton.setAttribute(
                'aria-expanded',
                false
            );

            this.allyHandles.keyHandle.disengage();
        }
    }

    /**
     * Opens the overflow menu
     * @method
     */
    openOverflowMenu() {
        if (this.overflowMenuActive) {
            this.navigationOverflowMenuContainer.classList.add(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_OPEN_CLASS
            );
        }
    }

    /**
     * Function that shows or hides the overflow menu
     * @param {bool} shouldShowOverflowMenu
     */
    showOverflowMenu(shouldShowOverflowMenu) {
        if (shouldShowOverflowMenu) {
            this.navigationOverflowMenuWrapper.classList.add(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_ACTIVE_CLASS
            );
        } else {
            this.navigationOverflowMenuWrapper.classList.remove(
                OverflowButton.TAB_NAVIGATION_OVERFLOW_MENU_ACTIVE_CLASS
            );
        }
    }
}

export default function _initOverflowButton() {
    const buttonExist = !!document.querySelector(BUTTON_WRAPPER);

    function initialise() {
        if (buttonExist) {
            const overflowButtons = document.querySelectorAll(BUTTON_WRAPPER);

            overflowButtons.forEach((button) => {
                // eslint-disable-next-line no-param-reassign
                button.instance = new OverflowButton(button);
            });
        }
    }

    initialise();
}

_initOverflowButton();
